import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const COLORS = {
  textPrimary: '#E7EEF8',
  textSecondary: '#97A6BA',
  textMuted: '#6F7E95',
  highlight: '#36C2B4',
  treeLine: 'rgba(134, 152, 180, 0.3)',
  treeLineHighlight: 'rgba(54, 194, 180, 0.48)',
  borderLight: 'rgba(134, 152, 180, 0.3)',
};

const THEME_COLORS = {
  blue: {
    highlight: '#36C2B4',
    treeLine: 'rgba(54, 194, 180, 0.24)',
    treeLineHighlight: 'rgba(157, 106, 248, 0.42)',
    border: 'rgba(54, 194, 180, 0.34)',
    panelBg: 'rgba(9, 16, 29, 0.74)',
  },
  orange: {
    highlight: '#F2A060',
    treeLine: 'rgba(242, 160, 96, 0.24)',
    treeLineHighlight: 'rgba(157, 106, 248, 0.42)',
    border: 'rgba(242, 160, 96, 0.34)',
    panelBg: 'rgba(27, 21, 32, 0.64)',
  },
};

const InternationalBadge = () => (
  <Box component="span" sx={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    background: `linear-gradient(135deg, #36C2B4, #9D6AF8)`,
    border: `1px solid ${COLORS.borderLight}`,
    borderRadius: '12px',
    padding: '2px 8px',
    ml: 1,
    boxShadow: '0 2px 8px rgba(157, 106, 248, 0.3)',
  }}>
    <Box component="span" sx={{ fontSize: '14px' }}>🌍</Box>
    <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: COLORS.textPrimary }}>
      International
    </Typography>
  </Box>
);

const DomesticBadge = () => (
  <Box component="span" sx={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    background: `linear-gradient(135deg, #36C2B4, #9D6AF8)`,
    border: `1px solid ${COLORS.borderLight}`,
    borderRadius: '12px',
    padding: '2px 8px',
    ml: 1,
    boxShadow: '0 2px 8px rgba(157, 106, 248, 0.3)',
  }}>
    <Box component="span" sx={{ fontSize: '14px' }}>🇮🇳</Box>
    <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: COLORS.textPrimary }}>
      Domestic
    </Typography>
  </Box>
);

const PricingBreakdown = ({ 
  basePrice, 
  pucAmount, 
  pucPeriod = 1,
  gstAmount, 
  isInternational = false,
  monthlyDiscText = "",
  pucLabel,
  glowColor = '#36C2B4', // Default to teal, can be overridden for Elite card
  colorTheme = 'blue',
  basePriceLabelSuffix = "",
  basePriceValueLabel = "",
  pucValueLabel = "",
  containerWidth = '90%',
  gstValueLabel,
  showDomesticBadge = true,
  showPuc = true,
}) => {
  const palette = THEME_COLORS[colorTheme] || THEME_COLORS.blue;
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('₹', '₹');
  };

  const BreakdownRow = ({ label, amount, tooltip, isLast = false, highlight = false, valueLabel = "" }) => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.5,
      pl: 2,
      pr: 1,
      position: 'relative'
    }}>
      {/* Vertical line */}
      <Box sx={{
        position: 'absolute',
        left: '8px',
        top: isLast ? 0 : '50%',
        height: isLast ? '50%' : '100%',
        width: '2px',
        backgroundColor: highlight ? palette.treeLineHighlight : palette.treeLine
      }} />
      
      {/* Horizontal line */}
      <Box sx={{
        position: 'absolute',
        left: '8px',
        top: '50%',
        width: '10px',
        height: '2px',
        backgroundColor: highlight ? palette.treeLineHighlight : palette.treeLine
      }} />
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ 
          fontSize: '12px', 
          color: highlight ? palette.highlight : COLORS.textSecondary,
          fontWeight: highlight ? 600 : 400
        }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: '13px', color: COLORS.textMuted, cursor: 'help', opacity: 0.7 }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ 
        fontSize: '12px', 
        color: highlight ? palette.highlight : COLORS.textSecondary,
        fontWeight: highlight ? 600 : 500,
        fontFamily: valueLabel ? 'inherit' : 'monospace'
      }}>
        {valueLabel || formatCurrency(amount)}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ 
      mt: 1.5,
      mb: 1,
      width: containerWidth,
      mx: 'auto',
      backgroundColor: palette.panelBg,
      borderRadius: '8px',
      border: `1px solid ${palette.border}`,
      overflow: 'hidden'
    }}>
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${palette.treeLine}`
        }}
      >
        <Typography sx={{
          fontSize: '11px',
          color: COLORS.textMuted,
          textAlign: 'left',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          flex: '1 1 auto',
          minWidth: 0
        }}>
          {/* Domestic badge can be hidden on combined/add-on flows to keep labels concise. */}
          PRICE BREAKDOWN{isInternational ? <InternationalBadge /> : (showDomesticBadge ? <DomesticBadge /> : null)}
        </Typography>
        <Box sx={{
          ml: 1,
          flexShrink: 0,
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: glowColor,
          boxShadow: `0 0 8px ${glowColor}, 0 0 4px ${glowColor}`,
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
              transform: 'scale(1)'
            },
            '50%': {
              opacity: 0.6,
              transform: 'scale(0.95)'
            }
          }
        }} />
      </Box>
      
      <Box sx={{ 
        py: 0.5
      }}>
        <BreakdownRow 
          label={`Base Price${basePriceLabelSuffix}${monthlyDiscText}`}
          amount={basePrice}
          tooltip="Subscription base price for the selected plan"
          valueLabel={basePriceValueLabel}
        />
        
        {showPuc ? (
          <BreakdownRow 
            label={`${pucLabel || `NSE PUC ${isInternational ? '(International)' : '(Domestic)'}`}${pucPeriod > 1 ? ` × ${pucPeriod}` : ''}`}
            amount={pucAmount * pucPeriod}
            tooltip={`Per User Cost: ₹${pucAmount}/month × ${pucPeriod} month${pucPeriod > 1 ? 's' : ''} = ₹${pucAmount * pucPeriod}`}
            valueLabel={pucValueLabel}
            highlight={true}
          />
        ) : null}
        
        <BreakdownRow 
          label="GST (18%)"
          amount={gstAmount}
          valueLabel={gstValueLabel}
          tooltip="Goods and Services Tax at 18%"
          isLast={true}
        />
      </Box>
    </Box>
  );
};

export default PricingBreakdown;